package fyi.moko.hotpl.domain.model

/**
 * Where each thing the reviewers asked for currently stands.
 *
 * ── 왜 상태만으로는 부족한가 ──────────────────────────────
 *  · 리뷰어는 코드를 모른다. 구글 지도나 애플 지도에서 당연히 되는 일이 여기서 안 되면,
 *    그건 "아직 안 만든 것"이 아니라 "고장난 것"으로 읽힌다. 그래서 못 하고 있는 항목에는
 *    반드시 한 줄짜리 이유를 붙인다 — 변명이 아니라, 리뷰어가 다음에 무엇을 물어볼지
 *    (재촉할지, 다른 방법을 제안할지) 정하는 데 필요한 정보다.
 *  · [note]에 기술 용어를 쓰지 않는다. "K/N interop", "actual", "Room 마이그레이션"은
 *    리뷰어에게 아무 의미가 없고, 설명하는 척하면서 설명하지 않는 문장이 된다.
 *  · 상태를 셋으로만 둔 것도 같은 이유다. 단계를 늘리면 읽는 쪽이 그 차이를 해석해야 한다.
 */
enum class RequestStatus(val label: String) {
    /** Shipped and testable in this build. */
    DONE("Done"),

    /** Partly there, or coming — [ReviewerRequest.effort] says how much is left. */
    IN_PROGRESS("In progress"),

    /** The obvious fix would make something else worse; the approach is being reconsidered. */
    RETHINKING("Needs rethinking"),
}

/**
 * Roughly how much work is left, for a reader deciding what to push on.
 * Only meaningful while a request is [RequestStatus.IN_PROGRESS].
 */
enum class RequestEffort(val label: String) {
    SMALL("small job"),
    MEDIUM("medium job"),
    LARGE("big job"),
}

data class ReviewerRequest(
    /** What was asked, in the reviewer's own words rather than the code's. */
    val request: String,
    val status: RequestStatus,
    val effort: RequestEffort? = null,
    /** One plain sentence: where to go and try it, or why it isn't as simple as it looks. */
    val note: String,
)

/** Standing status of reviewer feedback — not a per-build list, so it lives outside [Changelog]. */
object ReviewerRequests {

    val all: List<ReviewerRequest> = listOf(
        ReviewerRequest(
            request = "Sort by what's closest to me.",
            status = RequestStatus.DONE,
            note = "Journal's filter bar has a Nearest sort, and Directory orders results by real distance.",
        ),
        ReviewerRequest(
            request = "A button that just shows me what's nearby.",
            status = RequestStatus.DONE,
            note = "Directory → \"Near me\". It needs no search text, and you can narrow it to one category.",
        ),
        ReviewerRequest(
            request = "Plain categories — restaurant, cafe, bakery, bar.",
            status = RequestStatus.DONE,
            note = "All four filter both Journal and Directory.",
        ),
        ReviewerRequest(
            request = "The map opened on a place two cities away.",
            status = RequestStatus.DONE,
            note = "It opens on where you are now. It only jumps to a saved place when you tap that place.",
        ),
        ReviewerRequest(
            request = "When I look back at a place I want to know what I ate.",
            status = RequestStatus.DONE,
            note = "A place's photos swipe across the top, and \"What you ate\" gathers the food shots " +
                "from each visit with the date underneath.",
        ),
        ReviewerRequest(
            request = "Let me pick which photo is the main one.",
            status = RequestStatus.DONE,
            note = "Tap a photo in the place's gallery — the first option in the sheet makes it the cover.",
        ),
        ReviewerRequest(
            request = "Zoom buttons, not just pinching.",
            status = RequestStatus.DONE,
            note = "All three now — Android, iPhone and the web. Apple's map ships no zoom control " +
                "at all, so the iPhone pair had to be drawn by hand.",
        ),
        ReviewerRequest(
            request = "On iPhone, tapping a pin jumps straight into the place.",
            status = RequestStatus.DONE,
            note = "All three behave the same way now: the first tap names the place on a small card, " +
                "and tapping that card opens it.",
        ),
        ReviewerRequest(
            request = "The search box is hidden behind a button.",
            status = RequestStatus.IN_PROGRESS,
            effort = RequestEffort.SMALL,
            note = "Sorting and category filters are always visible now; the text box itself is still " +
                "behind the magnifying glass in the toolbar.",
        ),
        ReviewerRequest(
            request = "Browsing for nearby places stops working without a connection.",
            status = RequestStatus.IN_PROGRESS,
            effort = RequestEffort.LARGE,
            note = "Places you have already saved work offline. Browsing for new ones still asks Google " +
                "each time; keeping enough of the area on the phone to search it offline is the bigger piece.",
        ),
        ReviewerRequest(
            request = "Searching cafes turns up McDonald's.",
            status = RequestStatus.RETHINKING,
            note = "The category comes from Google's own label for each place, and Google files McCafé " +
                "under cafe. Hand-correcting the obvious ones is easy; doing it without quietly hiding " +
                "real cafes is the part that needs thought.",
        ),
    )
}
